import { useLayoutEffect, useRef, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useLocale } from '../../context/LocaleContext'
import { hasUnsavedChanges } from '../../hooks/useUnsavedChangesWarning'

export default function OrganizationSwitcher() {
  const { user, organizations = [], switchOrganization } = useAuth()
  const { t, url } = useLocale()
  const [pending, setPending] = useState(false), [error, setError] = useState('')
  const select = useRef<HTMLSelectElement>(null)
  useLayoutEffect(() => {
    if (pending || !error) return
    let active = true
    // Wait for the enabled DOM and the progress dialog's focus-restoration cleanup.
    queueMicrotask(() => { if (active) select.current?.focus({ preventScroll: true }) })
    return () => { active = false }
  }, [pending, error])
  const current = String(user?.tenantId ?? user?.tenant_id ?? '')
  const name = user?.organization?.name || organizations.find(org => org.id === current)?.name
  if (!name && organizations.length < 2) return null
  return <div className="min-w-0 max-w-[9rem] sm:max-w-[15rem]">
    {organizations.length < 2 ? <span title={name} className="block truncate text-sm font-semibold">{name}</span> : <>
      <label htmlFor="active-organization" className="sr-only">{t('Organisation active', 'Active organization')}</label>
      <select ref={select} id="active-organization" value={current} disabled={pending} aria-busy={pending} onChange={async event => {
        const target = event.target.value
        if (target === current || pending) return
        if (hasUnsavedChanges() && !window.confirm(t('Vos modifications non enregistrées seront perdues. Changer d’organisation ?', 'Your unsaved changes will be lost. Switch organization?'))) return
        setPending(true); setError('')
        try { await switchOrganization(target, url('/dashboard')) }
        catch (failure) { setError((failure as Error).message); setPending(false) }
      }} className="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100">
        {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
      </select>
      {pending && <span role="status" className="sr-only">{t('Changement d’organisation…', 'Switching organization…')}</span>}
    </>}
    {error && <p role="alert" className="absolute right-4 top-16 max-w-xs rounded-lg border bg-white p-3 text-sm text-red-800 shadow dark:bg-slate-900 dark:text-red-300">{error}</p>}
  </div>
}
